import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  TemplateRef,
  viewChild,
  inject,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  ActionItemElement,
  FileUploaderV2,
  RotecFormGroupConfigV2,
  RotecFormOptionsV2,
  SelectItemV2,
  deepClone,
  FormsModuleV2,
  SelectV2Module,
} from '@schenck/rotec-components';
import {
  KIND_OF_TASK_ITEMS,
  PRIORITY_ITEMS,
  RESPONSIBLE_COMPANY_ITEMS,
} from 'src/utils/constants/upsert-task.const';
import { DurationUnitNames, DurationUnit } from 'src/utils/constants/duration.const';
import {
  Components,
  componentToFunctionMap,
  getComponentMsgid,
  getFunctionGroupMsgid,
  FunctionGroup,
} from 'src/components/components-and-function-group';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs/operators';
import { MachineTaskUpsertModel } from '../../upsert-machine-task-wizard.component';
import { MachineStepBaseComponent } from '../machine-step-base.component';
import { pipe } from 'rxjs';

@Component({
  selector: 'ma-machine-description-step',
  templateUrl: './description-step.component.html',
  styleUrls: ['./description-step.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModuleV2, SelectV2Module],
})
export class MachineDescriptionStepComponent extends MachineStepBaseComponent implements OnInit {
  private readonly translate = inject(TranslateService);

  public readonly durationMinutesLabel =
    viewChild<TemplateRef<HTMLElement>>('durationMinutesLabel');
  public readonly actionSelect = viewChild<ActionItemElement>('actionSelect');
  @Input() public fileUploader: FileUploaderV2;

  public descriptionFields: RotecFormGroupConfigV2<MachineTaskUpsertModel>;

  public isOtherComponentSelected = false;

  public descriptionFormOptions: RotecFormOptionsV2 = {
    formId: 'descriptionFormMachineTask',
    props: { labelMinWidth: '250px' },
  };

  public durationUnits: SelectItemV2[] = [
    { name: DurationUnitNames[DurationUnit.MIN], value: DurationUnit.MIN },
    { name: DurationUnitNames[DurationUnit.H], value: DurationUnit.H },
  ];

  public toggleUnitSelect(): void {
    this.actionSelect()?.toggleSelect(null, true);
  }

  public durationUnit: DurationUnit = DurationUnit.MIN;

  public changeDurationUnit({ value }: SelectItemV2): void {
    this.durationUnit = value as DurationUnit;
    this.machineTaskModel.expectedDurationUnit = this.durationUnit;

    const expectedDurationValue = this.stepForm.get('expectedDurationMinutes').value;
    if (value === DurationUnit.H) {
      // convert minutes to hours
      this.stepForm.get('expectedDurationMinutes').setValue(expectedDurationValue / 60);
    } else {
      // convert hours to minutes
      this.stepForm.get('expectedDurationMinutes').setValue(expectedDurationValue * 60);
    }
  }

  public componentItems: SelectItemV2<Components>[] = Object.values(Components)
    .map(
      (value: Components) =>
        <SelectItemV2<Components>>{ name: this.translate.instant(getComponentMsgid(value)), value }
    )
    .sort((a, b) => {
      if ((a.value === Components.Machine) !== (b.value === Components.Machine)) {
        return a.value === Components.Machine ? -1 : 1;
      }
      if ((a.value === Components.OtherComponent) !== (b.value === Components.OtherComponent)) {
        return a.value === Components.OtherComponent ? 1 : -1;
      }
      return (a.name || '').localeCompare(b.name || '');
    });

  public functionGroupItems: SelectItemV2[] = Object.values(FunctionGroup).map(
    (value: FunctionGroup) => <SelectItemV2>{ name: getFunctionGroupMsgid(value), value }
  );

  constructor() {
    super();
  }

  public ngOnInit(): void {
    this.setupDescriptionForm();

    if (this.machineTaskModel.expectedDurationUnit === DurationUnit.H) {
      this.durationUnit = DurationUnit.H;
    }
  }

  private setupDescriptionForm(): void {
    this.descriptionFields = [
      { key: 'id' },
      {
        type: 'file-upload-v2',
        key: 'pictureIds',
        props: {
          label: 'Image, Add or Drag and Drop Images',
          service: this.fileUploader,
          acceptedFileFormats: ['.svg', '.png', '.jpg', '.jpeg'],
          fallbackImageSrc: './assets/img/favicon.ico',
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'title',
        props: {
          label: 'Title',
          placeholder: '-',
          required: true,
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'schenckId',
        props: {
          label: 'Task ID Schenck',
          placeholder: '-',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'customerId',
        props: {
          label: 'Task ID Customer',
          placeholder: '-',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'textarea-v2',
        key: 'description',
        props: {
          label: 'Description',
          placeholder: '-',
          minHeight: '100px',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'notice',
        props: {
          label: 'Notice',
          placeholder: '-',
          darkMode: true,
        },
      },
      {
        type: 'select-v2',
        key: 'priority',
        props: {
          label: 'Priority',
          translateNames: true,
          placeholder: 'Select priority',
          required: true,
          items: deepClone(PRIORITY_ITEMS),
          darkMode: true,
        },
      },
      {
        type: 'select-v2',
        key: 'component',
        props: {
          label: 'Component',
          placeholder: 'Select Component',
          items: this.componentItems,
          darkMode: true,
          secondary: true,
        },
        value$: pipe(
          filter((componentName: Components | null) => componentName !== null),
          map((componentName: Components) => {
            const functionGroup = componentToFunctionMap.get(componentName);

            this.isOtherComponentSelected = componentName === Components.OtherComponent;

            if (functionGroup) {
              return {
                functionGroup,
              };
            }
          })
        ),
      },
      {
        type: 'select-v2',
        key: 'functionGroup',
        props: {
          label: 'Function Group',
          translateNames: true,
          placeholder: '-',
          readonly: true,
          items: this.functionGroupItems,
          darkMode: true,
          secondary: true,
        },
      },
      {
        type: 'textarea-v2',
        key: 'customComponent',
        props: {
          label: 'Custom Component',
          placeholder: '-',
          minHeight: '50px',
          darkMode: true,
        },
        expressionProperties: {
          'props.visible': () => !!this.isOtherComponentSelected,
        },
      },
      {
        type: 'select-v2',
        key: 'type',
        props: {
          label: 'Task Type',
          translateNames: true,
          placeholder: 'Select task type',
          items: deepClone(KIND_OF_TASK_ITEMS),
          darkMode: true,
          secondary: true,
        },
      },
      {
        type: 'textarea-v2',
        key: 'requiredTooling',
        props: {
          label: 'Required Tooling',
          placeholder: '-',
          minHeight: '100px',
          isAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'textarea-v2',
        key: 'possibleSpareParts',
        props: {
          label: 'Possible Spare Parts',
          placeholder: '-',
          minHeight: '100px',
          isAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'select-v2',
        key: 'responsibleCompany',
        props: {
          label: 'Responsible Company',
          translateNames: true,
          placeholder: 'Select responsible company',
          items: deepClone(RESPONSIBLE_COMPANY_ITEMS),
          required: true,
          darkMode: true,
        },
        defaultValue: 'Customer',
      },
      {
        type: 'input-v2',
        key: 'responsible',
        props: {
          label: 'Task Responsible',
          placeholder: '-',
          darkMode: true,
        },
      },
      {
        type: 'input-v2',
        key: 'executive',
        props: {
          label: 'Task Executive',
          placeholder: '-',
          isAdditionalField: true,
          isDefaultAdditionalField: true,
          darkMode: true,
        },
      },
      {
        type: 'number-input-v2',
        key: 'expectedDurationMinutes',
        props: {
          label: 'Expected Duration',
          afterLabelTemplate: this.durationMinutesLabel(),
          min: 0,
          isAdditionalField: true,
          darkMode: true,
        },
        expressionProperties: {
          'props.placeholder': () => {
            return DurationUnitNames[this.durationUnit];
          },
        },
        validators: {
          minute: {
            expression: (control: AbstractControl) =>
              !control.value || !isNaN(parseInt(control.value, 10)),
            message: 'Enter a valid number',
          },
        },
      },
      {
        type: 'textarea-v2',
        key: 'notes',
        props: {
          label: 'Notes',
          placeholder: '-',
          minHeight: '200px',
          darkMode: true,
        },
      },
    ];
  }
}
